import { APPLICATION_ROUTES } from '@/constants/constants';
import { FEATURE_FLAGS, isFeatureEnabled } from '@/constants/featureFlags';
import { IMondayContext } from '@/types/response/monday/context';
import { fetchCpqTemplatePdfUrl } from '@/utils/cpqTemplatePdfUrl';
import { buildJetSignTemplateEditorUrl } from '@/utils/jetSignTemplateEditorUrl';
import { openLinkInTab, openPreviewLinkInTab, showToast } from '@/utils/monday';
import { openProposalEditorInNewTab } from '@/utils/openProposalEditorInNewTab';
import { isAxiosError } from 'axios';

export type TemplateGalleryTemplateType = 'document' | 'proposal';

export interface TemplateGalleryListItem {
  _id: string;
  templateName: string;
  boardId: number;
  itemId?: number;
  envelopeId?: string;
  fileId?: string;
  fileType?: string;
  externalTemplateId?: string;
  templateType?: TemplateGalleryTemplateType;
}

/** Board-registered templates are not tied to an item-view envelope upload. */
export function isBoardStandaloneTemplate(
  template: TemplateGalleryListItem,
): boolean {
  return !template.envelopeId;
}

function resolveDocumentTemplateEditorUrl(params: {
  template: TemplateGalleryListItem;
  context: IMondayContext;
  sessionToken: string;
  isPreview?: boolean;
}): string | null {
  const { template, context, sessionToken, isPreview } = params;
  const fileId = template.fileId;
  if (!fileId) {
    return null;
  }

  if (isBoardStandaloneTemplate(template)) {
    return buildJetSignTemplateEditorUrl(
      template._id,
      fileId,
      sessionToken,
      { isPreview },
    );
  }

  const isCkEditorRedirectEnabled = isFeatureEnabled(
    FEATURE_FLAGS.CK_EDITOR_REDIRECT,
  );
  const fileType = template.fileType ?? 'pdf';

  if (!isCkEditorRedirectEnabled || fileType === 'pdf') {
    return APPLICATION_ROUTES.TEMPLATE_GALLERY_EDITOR({
      templateId: template._id,
      fileId,
      sessionToken,
      context,
      isPreview,
    });
  }

  return APPLICATION_ROUTES.WORD_EDITOR({
    envelopeId: template._id,
    itemId: context.itemId,
    fileId,
    fromTemplateGallery: true,
  });
}

export async function openTemplateGalleryEditor(
  template: TemplateGalleryListItem,
  context: IMondayContext,
  sessionToken: string,
): Promise<void> {
  if (template.templateType === 'proposal') {
    if (!template.externalTemplateId) {
      await showToast({
        message: 'Proposal template id is missing.',
        type: 'error',
      });
      return;
    }

    const didOpen = await openProposalEditorInNewTab({
      templateId: template.externalTemplateId,
      templateName: template.templateName,
      boardId: template.boardId,
    });

    if (!didOpen) {
      await showToast({
        message:
          'Could not open the proposal editor. Configure VITE_BOARD_VIEW_URL or check popup permissions.',
        type: 'error',
      });
    }
    return;
  }

  const url = resolveDocumentTemplateEditorUrl({
    template,
    context,
    sessionToken,
  });

  if (!url) {
    await showToast({
      message: template.fileId
        ? 'Template editor URL is not configured.'
        : 'Template file is missing.',
      type: 'error',
    });
    return;
  }

  await openLinkInTab({ url });
}

function proposalPreviewErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data as { message?: string };
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return 'Failed to open template preview.';
}

export async function openTemplateGalleryPreview(
  template: TemplateGalleryListItem,
  context: IMondayContext,
  sessionToken: string,
): Promise<void> {
  if (template.templateType === 'proposal') {
    if (!template.externalTemplateId) {
      await showToast({
        message: 'Proposal template id is missing.',
        type: 'error',
      });
      return;
    }

    try {
      const pdfUrl = await fetchCpqTemplatePdfUrl(template.externalTemplateId);
      const didOpen = openPreviewLinkInTab({ url: pdfUrl });
      if (!didOpen) {
        await showToast({
          message:
            'Could not open the preview in a new tab. Check popup permissions.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('[TemplateGallery] Failed to open proposal preview', error);
      const message = proposalPreviewErrorMessage(error);
      if (/not been generated|pdf has not/i.test(message)) {
        await showToast({
          message:
            'Save or publish this template in the editor before previewing.',
          type: 'error',
        });
        return;
      }
      await showToast({ message, type: 'error' });
    }
    return;
  }

  const url = resolveDocumentTemplateEditorUrl({
    template,
    context,
    sessionToken,
    isPreview: true,
  });

  if (!url) {
    await showToast({
      message: template.fileId
        ? 'Template preview URL is not configured.'
        : 'Template file is missing.',
      type: 'error',
    });
    return;
  }

  const didOpen = openPreviewLinkInTab({ url });
  if (!didOpen) {
    await showToast({
      message:
        'Could not open the preview in a new tab. Check popup permissions.',
      type: 'error',
    });
  }
}
